import React, { useEffect, useState, useMemo } from "react";
import { useSession, useFindMany } from "@gadgetinc/react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useSemesterFilter } from "../hooks/useSemesterFilter";
import SemesterFilterDropdown from "../components/SemesterFilterDropdown";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Select,
  Button,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Collapse,
  Checkbox,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";

// Helper function to extract shift location from various sources
const getShiftLocation = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.location) {
        return parsed.shift.location;
      }
    }
    if (shift.fisdapData?.location) {
      return shift.fisdapData.location;
    }
    if (shift.structuredData?.shift_location) {
      return shift.structuredData.shift_location;
    }
  } catch (e) {
    console.error("Error extracting location:", e);
  }
  return "Unknown Location";
};

// Helper function to extract patient count
const getPatientCount = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.document_info?.total_patients) {
        return parsed.shift.document_info.total_patients;
      }
    }
    if (shift.structuredData?.patients?.length) {
      return shift.structuredData.patients.length;
    }
    if (shift.patientEncounterCount) {
      return shift.patientEncounterCount;
    }
  } catch (e) {
    console.error("Error extracting patient count:", e);
  }
  return 0;
};

// Helper function to extract and aggregate impressions
const extractImpressions = (shifts, impressionType) => {
  const impressions = {};
  
  shifts.forEach((shift) => {
    try {
      // Try parsing from aiEvaluationResponse first
      let patients = [];
      
      if (shift.aiEvaluationResponse?.markdown) {
        try {
          const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
          if (parsed?.student_report?.per_patient_feedback) {
            patients = parsed.student_report.per_patient_feedback;
          }
        } catch (e) {
          console.error("Error parsing AI evaluation:", e);
        }
      }
      
      // Fallback to structuredData
      if (patients.length === 0 && shift.structuredData?.patients) {
        patients = shift.structuredData.patients;
      }
      
      patients.forEach((patient) => {
        const impression = patient[impressionType];
        if (impression) {
          impressions[impression] = (impressions[impression] || 0) + 1;
        }
      });
    } catch (e) {
      console.error("Error extracting impressions:", e);
    }
  });

  // Sort by frequency and return top 3
  return Object.entries(impressions)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([impression]) => impression);
};

// Format date helper
const formatDate = (date) => {
  if (!date) return "N/A";
  try {
    return new Date(date).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    return "N/A";
  }
};

// Helper to extract the actual shift date (not submission date)
const getShiftDate = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.document_info?.shift_date) {
        // The shift date is in format like "Oct 22, 2025"
        const dateStr = parsed.shift.document_info.shift_date;
        // Convert to a proper date object
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate)) {
          return parsedDate.toISOString();
        }
        return dateStr; // Return as-is if parsing fails
      }
    }
    if (shift.fisdapData?.shift_date) {
      return shift.fisdapData.shift_date;
    }
    if (shift.structuredData?.shift_date) {
      return shift.structuredData.shift_date;
    }
    // Fallback to submission date if shift date not found
    if (shift.submissionDate) {
      return shift.submissionDate;
    }
  } catch (e) {
    console.error("Error extracting shift date:", e);
  }
  return shift.submissionDate || null;
};

// Helper to check if shift is locked
const isShiftLocked = (shift) => {
  try {
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      return parsed?.shift?.shift_locked === true;
    }
    if (shift.fisdapData?.shift_locked) {
      return shift.fisdapData.shift_locked === true;
    }
    if (shift.structuredData?.shift_locked) {
      return shift.structuredData.shift_locked === true;
    }
  } catch (e) {
    console.error("Error checking shift lock status:", e);
  }
  return false;
};

// Helper to get duplicate info for a shift
const getDuplicateInfo = (shift, allShifts) => {
  if (!shift.sourceShiftId) return null;
  
  // Find all shifts with the same sourceShiftId
  const duplicates = allShifts.filter(
    s => s.sourceShiftId === shift.sourceShiftId
  );
  
  if (duplicates.length <= 1) return null;
  
  // Sort by submission date (oldest first)
  const sorted = [...duplicates].sort((a, b) => 
    new Date(a.submissionDate) - new Date(b.submissionDate)
  );
  
  // Find this shift's position
  const position = sorted.findIndex(s => s.id === shift.id) + 1;
  const isLatest = position === sorted.length;
  
  return {
    count: duplicates.length,
    position,
    isLatest
  };
};

// Status badge component
const StatusBadge = ({ verdict }) => {
  const colorScheme = {
    pass: "green",
    proficient: "green",
    acceptable: "green",
    exemplary: "green",
    fail: "red",
    needs_review: "yellow",
    required: "orange",
    indeterminate: "gray",
  };

  const label = {
    pass: "Pass",
    proficient: "Proficient",
    acceptable: "Acceptable",
    exemplary: "Exemplary",
    fail: "Fail",
    needs_review: "Needs Review",
    required: "Required",
    indeterminate: "Indeterminate",
  };

  return (
    <Badge colorScheme={colorScheme[verdict] || "gray"} size="sm">
      {label[verdict] || verdict}
    </Badge>
  );
};

// Shift detail modal
const ShiftDetailModal = ({ shift, relatedUserFiles = [], loadingRelatedFiles = false, isOpen, onClose }) => {
  if (!shift) return null;

  const sortedFiles = [...(relatedUserFiles || [])].sort((a, b) => {
    const aName = String(a?.originalFilename || a?.title || "").toLowerCase();
    const bName = String(b?.originalFilename || b?.title || "").toLowerCase();
    const aIsShiftReport = aName.includes("shift") && aName.includes("report");
    const bIsShiftReport = bName.includes("shift") && bName.includes("report");
    if (aIsShiftReport && !bIsShiftReport) return -1;
    if (!aIsShiftReport && bIsShiftReport) return 1;
    return aName.localeCompare(bName);
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Shift Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontWeight="bold">Source Shift ID:</Text>
              <Text>{shift.sourceShiftId}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Submission Date:</Text>
              <Text>{formatDate(shift.submissionDate)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Location:</Text>
              <Text>{getShiftLocation(shift)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Patient Count:</Text>
              <Text>{getPatientCount(shift)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Status:</Text>
              <StatusBadge verdict={shift.auditVerdict} />
            </Box>
            <Box>
              <Text fontWeight="bold">Attachments:</Text>
              {loadingRelatedFiles ? (
                <Spinner size="sm" mt={2} />
              ) : sortedFiles.length === 0 ? (
                <Text color="gray.500">No attachments found for this shift.</Text>
              ) : (
                <VStack align="stretch" spacing={2} mt={2}>
                  {sortedFiles.map((file) => (
                    <HStack key={file.id} justify="space-between" p={2} borderWidth={1} borderRadius="md">
                      <Text fontSize="sm">
                        {file.originalFilename || file.title || "Attachment"}
                      </Text>
                      {file.file?.url ? (
                        <Button
                          as="a"
                          href={file.file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                        >
                          Open
                        </Button>
                      ) : (
                        <Badge colorScheme="gray">No URL</Badge>
                      )}
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
            {shift.aiEvaluationResponse?.markdown && (
              <Box>
                <Text fontWeight="bold">AI Feedback:</Text>
                <Box
                  p={3}
                  bg="gray.800"
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                  borderWidth={1}
                  borderColor="gray.600"
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.100">
                    {shift.aiEvaluationResponse.markdown}
                  </Text>
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    #printable-report, #printable-report * {
      visibility: visible;
    }
    #printable-report {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px;
    }
    .print-column {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
`;

const buildStudentDisplayName = (membership) => {
  if (membership?.user?.firstName || membership?.user?.lastName) {
    return `${membership.user?.firstName || ""} ${membership.user?.lastName || ""}`.trim();
  }
  return membership?.userEmail || "Unknown Student";
};

const sortStudentMemberships = (memberships = []) => {
  return [...memberships].sort((a, b) => {
    const aLast = String(a?.user?.lastName || "").toLowerCase();
    const bLast = String(b?.user?.lastName || "").toLowerCase();
    if (aLast !== bLast) return aLast.localeCompare(bLast);

    const aFirst = String(a?.user?.firstName || "").toLowerCase();
    const bFirst = String(b?.user?.firstName || "").toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

    const aEmail = String(a?.user?.email || a?.userEmail || "").toLowerCase();
    const bEmail = String(b?.user?.email || b?.userEmail || "").toLowerCase();
    return aEmail.localeCompare(bEmail);
  });
};

const getSubmissionDateValue = (shift) => {
  return shift?.submissionDate || shift?.createdAt || null;
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isPassingShift = (shift) => {
  const verdict = String(shift?.auditVerdict || "").toLowerCase();
  const evaluation = String(shift?.evaluationStatus || "").toLowerCase();
  const passing = new Set(["pass", "proficient", "acceptable", "exemplary"]);
  return passing.has(verdict) || passing.has(evaluation);
};

const getShiftStudentId = (shift) => {
  return shift?.submittedById || shift?.submittedBy?.id || null;
};

const getShiftTypeKey = (shift) => {
  const type = String(shift?.shiftType || "").toLowerCase();
  if (type.includes("lab")) return "lab";
  if (type.includes("field")) return "field";
  if (type.includes("clinical") || type.includes("hospital")) return "clinical";
  return null;
};

const applyAdvancedShiftFilters = (shifts = [], countDuplicates, countDrafts) => {
  let result = [...shifts];

  if (!countDrafts) {
    result = result.filter((shift) => {
      if (shift.isDraft === true) return false;
      return isShiftLocked(shift);
    });
  }

  if (!countDuplicates) {
    const grouped = {};
    result.forEach((shift) => {
      const key = shift.sourceShiftId || shift.id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(shift);
    });

    result = Object.values(grouped).map((group) =>
      group.sort((a, b) => {
        const bySubmission = toTimestamp(b.submissionDate) - toTimestamp(a.submissionDate);
        if (bySubmission !== 0) return bySubmission;
        const byCreated = toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
        if (byCreated !== 0) return byCreated;
        const byUpdated = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
        if (byUpdated !== 0) return byUpdated;
        return String(b.id).localeCompare(String(a.id));
      })[0]
    );
  }

  return result;
};

const getDateRangeBounds = (dateRange) => {
  const bounds = { from: null, to: null };
  if (dateRange?.from) {
    const from = new Date(`${dateRange.from}T00:00:00`);
    if (!Number.isNaN(from.getTime())) bounds.from = from.toISOString();
  }
  if (dateRange?.to) {
    const to = new Date(`${dateRange.to}T23:59:59.999`);
    if (!Number.isNaN(to.getTime())) bounds.to = to.toISOString();
  }
  return bounds;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatSemesterBadge = (semester) => {
  if (!semester) return "All semesters";
  const start = toDateInputValue(semester.startDate);
  const end = toDateInputValue(semester.endDate);
  if (start && end) return `${semester.name} (${start} to ${end})`;
  return semester.name || "Selected semester";
};

export default function FisdapReports() {
  const navigate = useNavigate();
  const session = useSession(api);
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [selectedShift, setSelectedShift] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [countDuplicates, setCountDuplicates] = useState(false);
  const [countDrafts, setCountDrafts] = useState(false);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    semesters,
    selectedSemesterId,
    setSelectedSemesterId,
    semesterDateRange,
    loading: semestersLoading,
  } = useSemesterFilter(selectedCohortId);
  const selectedSemester = useMemo(
    () => semesters.find((s) => s.id === selectedSemesterId) || null,
    [semesters, selectedSemesterId]
  );

  // Check user access
  const userRoles = session?.user?.roles || [];
  const isAdmin =
    userRoles.some((role) => role.key === "admin") ||
    userRoles.some((role) => role.key === "system-admin");

  // Fetch user's cohort memberships to check for FISDAP access
  const [{ data: userMemberships, fetching: fetchingMemberships, error: membershipError }] =
    useFindMany(api.cohortMembership, {
      filter: {
        userId: { equals: session?.user?.id },
        hasFisdapReviewAccess: { equals: true },
        active: { equals: true },
      },
      select: {
        id: true,
        cohortId: true,
        hasFisdapReviewAccess: true,
      },
    });

  const hasAccess = isAdmin || (userMemberships && userMemberships.length > 0);

  // Get accessible cohort IDs
  const accessibleCohortIds = useMemo(() => {
    if (isAdmin) return null; // Admin can see all
    return userMemberships?.map((m) => m.cohortId) || [];
  }, [userMemberships, isAdmin]);

  // Fetch cohorts
  const cohortFilter = useMemo(() => {
    const filter = { isActive: { equals: true } };
    if (!isAdmin && accessibleCohortIds.length > 0) {
      filter.id = { in: accessibleCohortIds };
    }
    return filter;
  }, [isAdmin, accessibleCohortIds]);

  const [{ data: cohorts, fetching: fetchingCohorts, error: cohortsError }] =
    useFindMany(api.cohort, {
      filter: cohortFilter,
      select: {
        id: true,
        displayName: true,
        name: true,
        institution: true,
      },
      sort: { displayName: "Ascending" },
    });

  // Fetch students in selected cohort
  const [{ data: studentMemberships, fetching: fetchingStudents, error: studentMembershipsError }] = useFindMany(
    api.cohortMembership,
    {
      filter: {
        cohortId: { equals: selectedCohortId },
        primaryRole: { equals: "student" },
        active: { equals: true },
        userId: { isSet: true }, // Only get memberships with valid users
        user: { id: { isSet: true } }, // Additional check: user relationship must exist
      },
      select: {
        id: true,
        userId: true,
        userEmail: true,
        validLabShifts: true,
        validClinicalShifts: true,
        validFieldShifts: true,
        validTeamLeadShifts: true,
        totalValidShifts: true,
        totalLabSubmissions: true,
        totalClinicalSubmissions: true,
        totalFieldSubmissions: true,
        totalTeamLeadSubmissions: true,
        totalSubmissions: true,
        erroredShifts: true,
        totalShiftCount: true,
        lastShiftSubmittedAt: true,
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
      },
      sort: { userEmail: "Ascending" },
    },
    { pause: !selectedCohortId }
  );

  const sortedStudentMemberships = useMemo(() => {
    return sortStudentMemberships(studentMemberships || []);
  }, [studentMemberships]);

  useEffect(() => {
    if (!selectedCohortId) {
      setDateRange({ from: "", to: "" });
      return;
    }

    if (selectedSemesterId && semesterDateRange) {
      setDateRange({
        from: toDateInputValue(semesterDateRange.startDate),
        to: toDateInputValue(semesterDateRange.endDate),
      });
    }
  }, [selectedCohortId, selectedSemesterId, semesterDateRange]);

  // Fetch shift processing records for students
  const studentIds = useMemo(() => {
    return sortedStudentMemberships.map((m) => m.userId).filter(Boolean);
  }, [sortedStudentMemberships]);

  const shiftFilter = useMemo(() => {
    const filter = {};
    const { from, to } = getDateRangeBounds(dateRange);
    if (studentIds.length > 0) {
      filter.submittedById = { in: studentIds };
    }
    if (from) {
      filter.submissionDate = { greaterThanOrEqual: from };
    }
    if (to) {
      filter.submissionDate = {
        ...filter.submissionDate,
        lessThanOrEqual: to,
      };
    }
    return filter;
  }, [studentIds, dateRange]);

  // Fetch shifts for expanded student only
  const expandedStudentFilter = useMemo(() => {
    if (!expandedStudentId) return {};
    const { from, to } = getDateRangeBounds(dateRange);
    const filter = {
      submittedById: { equals: expandedStudentId }
    };
    if (from) {
      filter.submissionDate = { greaterThanOrEqual: from };
    }
    if (to) {
      filter.submissionDate = {
        ...filter.submissionDate,
        lessThanOrEqual: to,
      };
    }
    return filter;
  }, [expandedStudentId, dateRange]);

  const [{ data: expandedStudentShifts, fetching: fetchingExpandedShifts }] = useFindMany(
    api.shiftProcessing,
    {
      filter: expandedStudentFilter,
      select: {
        id: true,
        submittedById: true,
        sourceShiftId: true,
        submissionDate: true,
        submissionEmail: true,
        originalFilename: true,
        auditVerdict: true,
        requiresReview: true,
        evaluationStatus: true,
        patientEncounterCount: true,
        shiftType: true,
        isDraft: true,
        submittedBy: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
        aiEvaluationResponse: {
          markdown: true
        },
        structuredData: true,
        fisdapData: true,
        emailMetadata: true,
        createdAt: true,
        updatedAt: true,
      },
      sort: { submissionDate: "Descending" },
      first: 250,
    },
    { pause: !expandedStudentId }
  );

  const [{ data: relatedUserFiles, fetching: loadingRelatedFiles }] = useFindMany(
    api.userFile,
    {
      filter: selectedShift?.id
        ? {
            shiftProcessingId: { equals: selectedShift.id },
            status: { equals: "active" },
          }
        : { id: { equals: "never" } },
      select: {
        id: true,
        title: true,
        originalFilename: true,
        file: { url: true, fileName: true, mimeType: true },
      },
      first: 50,
    },
    { pause: !selectedShift?.id }
  );

  // Fetch all shifts for the selected cohort/date scope for accurate summary counts.
  const [{ data: allShifts, fetching: fetchingAllShifts }] = useFindMany(
    api.shiftProcessing,
    {
      filter: shiftFilter,
      select: {
        id: true,
        submittedById: true,
        sourceShiftId: true,
        submissionDate: true,
        submissionEmail: true,
        originalFilename: true,
        auditVerdict: true,
        requiresReview: true,
        evaluationStatus: true,
        patientEncounterCount: true,
        shiftType: true,
        isDraft: true,
        submittedBy: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
        aiEvaluationResponse: {
          markdown: true
        },
        structuredData: true,
        fisdapData: true,
        emailMetadata: true,
        createdAt: true,
        updatedAt: true,
      },
      sort: { submissionDate: "Descending" },
      first: 5000,
    },
    { pause: studentIds.length === 0 }
  );

  const shifts = isPrinting ? allShifts : expandedStudentShifts;
  const fetchingShifts = isPrinting ? fetchingAllShifts : fetchingExpandedShifts;

  // Filter shifts based on advanced filter settings
  const filteredShifts = useMemo(() => {
    if (!shifts) return [];
    return applyAdvancedShiftFilters(shifts, countDuplicates, countDrafts);
  }, [shifts, countDuplicates, countDrafts]);

  const filteredAllShifts = useMemo(() => {
    if (!allShifts) return [];
    return applyAdvancedShiftFilters(allShifts, countDuplicates, countDrafts);
  }, [allShifts, countDuplicates, countDrafts]);

  // Calculate shift type counts from current filtered semester/date scope.
  const shiftTypeCounts = useMemo(() => {
    const empty = { lab: 0, field: 0, clinical: 0, teamLead: 0, totalValid: 0, totalSubmissions: 0 };
    if (!selectedCohortId) return empty;

    const scopedShifts = selectedStudentId
      ? filteredAllShifts.filter((s) => getShiftStudentId(s) === selectedStudentId)
      : filteredAllShifts;

    return scopedShifts.reduce((acc, shift) => {
      const key = getShiftTypeKey(shift);
      if (key) acc[key] += 1;
      acc.totalValid += 1;
      acc.totalSubmissions += 1;
      return acc;
    }, empty);
  }, [selectedCohortId, filteredAllShifts, selectedStudentId]);

  const studentSummaryById = useMemo(() => {
    const summary = {};
    for (const shift of filteredAllShifts) {
      const studentId = getShiftStudentId(shift);
      if (!studentId) continue;
      if (!summary[studentId]) {
        summary[studentId] = { total: 0, pass: 0 };
      }
      summary[studentId].total += 1;
      if (isPassingShift(shift)) {
        summary[studentId].pass += 1;
      }
    }
    return summary;
  }, [filteredAllShifts]);

  const studentShiftTypeCountsById = useMemo(() => {
    const summary = {};
    for (const shift of filteredAllShifts) {
      const studentId = getShiftStudentId(shift);
      if (!studentId) continue;
      if (!summary[studentId]) {
        summary[studentId] = { lab: 0, clinical: 0, field: 0, teamLead: 0, total: 0 };
      }

      const key = getShiftTypeKey(shift);
      if (key) summary[studentId][key] += 1;
      summary[studentId].total += 1;
    }
    return summary;
  }, [filteredAllShifts]);

  // Map student memberships to display data
  const shiftsByStudent = useMemo(() => {
    const grouped = {};
    if (!sortedStudentMemberships.length) return grouped;

    sortedStudentMemberships.forEach((membership) => {
      if (selectedStudentId === "" || membership.userId === selectedStudentId) {
        // Get shifts only if this student is expanded or we're printing
        const studentShifts = (isPrinting && filteredShifts) 
          ? filteredShifts.filter((shift) => getShiftStudentId(shift) === membership.userId)
          : (expandedStudentId === membership.userId && filteredShifts)
          ? filteredShifts
          : [];

        const scopedTypeCounts = studentShiftTypeCountsById[membership.userId] || {
          lab: 0,
          clinical: 0,
          field: 0,
          teamLead: 0,
          total: 0,
        };

        grouped[membership.userId] = {
          membership,
          shifts: studentShifts,
          scopedTypeCounts,
          passCount: studentSummaryById[membership.userId]?.pass || 0,
          totalCount: studentSummaryById[membership.userId]?.total ??
            (membership.totalValidShifts ?? membership.totalShiftCount ?? 0),
        };
      }
    });

    return grouped;
  }, [sortedStudentMemberships, selectedStudentId, filteredShifts, expandedStudentId, isPrinting, studentSummaryById, studentShiftTypeCountsById]);

  // Calculate cohort stats from membership data
  const cohortStats = useMemo(() => {
    if (!selectedCohortId || !sortedStudentMemberships.length) {
      return null;
    }

    const membershipsForStats = selectedStudentId
      ? sortedStudentMemberships.filter((m) => m.userId === selectedStudentId)
      : sortedStudentMemberships;

    const totalStudents = membershipsForStats.length;
    const totalShifts = membershipsForStats.reduce(
      (sum, m) => sum + (m.totalShiftCount || 0),
      0
    );
    
    // Get date range from lastShiftSubmittedAt if available
    const submissionDates = membershipsForStats
      .map(m => m.lastShiftSubmittedAt)
      .filter(d => d)
      .map(d => new Date(d))
      .filter(d => !isNaN(d));
    
    const minDate = submissionDates.length > 0 ? new Date(Math.min(...submissionDates)) : null;
    const maxDate = submissionDates.length > 0 ? new Date(Math.max(...submissionDates)) : null;

    return {
      totalStudents,
      totalShifts,
      dateRange:
        minDate && maxDate
          ? `${formatDate(minDate)} - ${formatDate(maxDate)}`
          : "No submissions",
    };
  }, [selectedCohortId, sortedStudentMemberships, selectedStudentId]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  const toggleStudent = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
      setExpandedStudentId(null);
    } else {
      newExpanded.add(studentId);
      setExpandedStudentId(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const openShiftDetail = (shift) => {
    setSelectedShift(shift);
    onOpen();
  };

  if (fetchingMemberships && !isAdmin) {
    return (
      <Box p={8} display="flex" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You do not have permission to view FISDAP reports. Please contact your
              administrator.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} minH="100vh">
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" mb={2}>
          <HStack spacing={4}>
            <Button
              leftIcon={<ArrowBackIcon />}
              colorScheme="gray"
              variant="outline"
              onClick={() => navigate('/fisdap-review')}
              size="sm"
            >
              Back to Review
            </Button>
            <Heading size="lg">FISDAP Clinical Reports</Heading>
          </HStack>
        </HStack>

        {/* Filter Section */}
        <Box p={4} borderWidth={1} borderRadius="md" shadow="md" borderColor="gray.600">
          <VStack align="stretch" spacing={3}>
            <HStack spacing={3} wrap="wrap">
              <Box flex={1} minW="200px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Cohort
                </Text>
                <Select
                  size="sm"
                  placeholder="Select cohort"
                  value={selectedCohortId}
                  onChange={(e) => {
                    setSelectedCohortId(e.target.value);
                    setSelectedStudentId("");
                  }}
                  borderColor="gray.600"
                >
                  {cohorts?.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.displayName || cohort.name}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box flex={1} minW="200px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Semester
                </Text>
                <SemesterFilterDropdown
                  cohortId={selectedCohortId}
                  semesters={semesters}
                  selectedSemesterId={selectedSemesterId}
                  loading={semestersLoading}
                  onChange={(id, range) => {
                    setSelectedSemesterId(id);
                    if (!id || !range) {
                      setDateRange({ from: "", to: "" });
                      return;
                    }
                    setDateRange({
                      from: toDateInputValue(range.startDate),
                      to: toDateInputValue(range.endDate),
                    });
                  }}
                />
              </Box>

              <Box flex={1} minW="200px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Student
                </Text>
                <Select
                  size="sm"
                  placeholder="All Students"
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  isDisabled={!selectedCohortId}
                  borderColor="gray.600"
                >
                  {sortedStudentMemberships.map((membership) => (
                    <option key={membership.userId} value={membership.userId}>
                      {buildStudentDisplayName(membership)}
                      {membership.user?.email ? ` (${membership.user.email})` : ""}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box flex={1} minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  From Date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={dateRange.from}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, from: e.target.value })
                  }
                />
              </Box>

              <Box flex={1} minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  To Date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={dateRange.to}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, to: e.target.value })
                  }
                />
              </Box>

              <Box alignSelf="flex-end">
                <Button size="sm" onClick={handleRefresh}>
                  Refresh
                </Button>
              </Box>

              <Box alignSelf="flex-end">
                <Button
                  colorScheme="purple"
                  size="sm"
                  onClick={handlePrint}
                  isDisabled={!selectedCohortId || !shifts || shifts.length === 0}
                >
                  Print Report
                </Button>
              </Box>
            </HStack>
          </VStack>
        </Box>

        {/* Advanced Filters */}
        <Box>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            mb={2}
          >
            {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
          </Button>
          <Collapse in={showAdvancedFilters} animateOpacity>
            <Box p={4} borderWidth={1} borderRadius="md" bg="gray.800" borderColor="gray.600">
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Filter Options
                </Text>
                <Checkbox
                  isChecked={countDuplicates}
                  onChange={(e) => setCountDuplicates(e.target.checked)}
                >
                  <Text fontSize="sm">Count Duplicate Submissions</Text>
                </Checkbox>
                <Checkbox
                  isChecked={countDrafts}
                  onChange={(e) => setCountDrafts(e.target.checked)}
                >
                  <Text fontSize="sm">Count Draft Shifts</Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  By default, only the latest submission per shift and locked shifts are counted.
                </Text>
              </VStack>
            </Box>
          </Collapse>
        </Box>

        {/* Cohort Stats Header */}
        {selectedCohortId && cohortStats && (
          <Box p={4} borderWidth={1} borderRadius="md" bg="blue.900" shadow="sm" borderColor="blue.700">
            <HStack mb={3}>
              <Text fontSize="sm" color="gray.300">Selected semester:</Text>
              <Badge colorScheme={selectedSemester ? "green" : "gray"}>
                {formatSemesterBadge(selectedSemester)}
              </Badge>
            </HStack>
            <HStack spacing={6} wrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Total Students
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {cohortStats.totalStudents}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Total Valid Shifts
                </Text>
                <Text fontSize="3xl" fontWeight="extrabold" color="green.400">
                  {shiftTypeCounts.totalValid}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Lab Shifts
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftTypeCounts.lab}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Clinical Shifts
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftTypeCounts.clinical}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Field Shifts
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftTypeCounts.field}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Team Lead Shifts
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftTypeCounts.teamLead}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Total Submissions
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftTypeCounts.totalSubmissions}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Submission Range
                </Text>
                <Text fontSize="lg" fontWeight="medium">
                  {cohortStats.dateRange}
                </Text>
              </Box>
            </HStack>
          </Box>
        )}

        {/* Error Display */}
        {studentMembershipsError && (
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <AlertTitle>Data Issue Detected</AlertTitle>
              <AlertDescription>
                Some student records could not be loaded. This usually means there are orphaned cohort memberships. Contact your administrator.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Loading States */}
        {(fetchingCohorts || fetchingStudents || fetchingShifts) && (
          <Box display="flex" justifyContent="center" p={4}>
            <Spinner />
          </Box>
        )}

        {/* No Cohort Selected */}
        {!selectedCohortId && !fetchingCohorts && (
          <Alert status="info">
            <AlertIcon />
            Select a cohort to view reports
          </Alert>
        )}

        {/* No Students Found */}
        {selectedCohortId &&
          !fetchingStudents &&
          sortedStudentMemberships.length === 0 && (
            <Alert status="warning">
              <AlertIcon />
              No students found in this cohort
            </Alert>
          )}

        {/* Student Accordion */}
        {selectedCohortId &&
          sortedStudentMemberships.length > 0 && (
            <Accordion allowMultiple>
              {Object.entries(shiftsByStudent).map(
                ([studentId, { membership, shifts: studentShifts, passCount, totalCount, scopedTypeCounts }]) => {
                  const passRate =
                    totalCount > 0
                      ? Math.round((passCount / totalCount) * 100)
                      : 0;

                  const primaryImpressions = extractImpressions(
                    studentShifts,
                    "primary_impression"
                  );
                  const secondaryImpressions = extractImpressions(
                    studentShifts,
                    "secondary_impression"
                  );

                  return (
                    <AccordionItem key={studentId} mb={2} borderRadius="md" shadow="sm" borderWidth={1} borderColor="gray.600">
                      <h2>
                        <AccordionButton onClick={() => toggleStudent(studentId)}>
                          <Box flex="1" textAlign="left">
                            <VStack align="start" spacing={1}>
                              <HStack spacing={3}>
                                <Text fontWeight="medium">
                                  {membership.user?.firstName && membership.user?.lastName
                                    ? `${membership.user.firstName} ${membership.user.lastName}`
                                    : membership.userEmail || 'Unknown Student'}
                                </Text>
                                <Badge colorScheme="blue">{totalCount} shifts</Badge>
                                {totalCount > 0 && (
                                  <Badge colorScheme="green">{passRate}% pass</Badge>
                                )}
                              </HStack>
                              <Text fontSize="sm" color="gray.600">
                                {membership.user?.email || membership.userEmail}
                              </Text>
                              {/* Shift count boxes */}
                              <HStack spacing={2} mt={2}>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Lab</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.lab || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Clinical</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.clinical || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Field</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.field || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Team Lead</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.teamLead || 0}</Text>
                                  </VStack>
                                </Box>
                              </HStack>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                All semesters totals:
                                {" "}
                                L {membership.validLabShifts || 0} |
                                {" "}C {membership.validClinicalShifts || 0} |
                                {" "}F {membership.validFieldShifts || 0} |
                                {" "}TL {membership.validTeamLeadShifts || 0} |
                                {" "}Total {membership.totalValidShifts || membership.totalShiftCount || 0}
                              </Text>
                            </VStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {fetchingExpandedShifts && expandedStudentId === studentId ? (
                          <Box display="flex" justifyContent="center" p={4}>
                            <Spinner size="sm" />
                          </Box>
                        ) : studentShifts.length === 0 ? (
                          <Alert status="info" size="sm">
                            <AlertIcon />
                            {totalCount > 0 
                              ? "Loading shift details..." 
                              : "No shifts submitted yet"}
                          </Alert>
                        ) : (
                          <Table size="sm" variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Shift Date</Th>
                                <Th>Shift ID</Th>
                                <Th width="50px" textAlign="center">Dup</Th>
                                <Th>Location</Th>
                                <Th>Pts</Th>
                                <Th width="40px" textAlign="center">🔒</Th>
                                <Th>Submitted</Th>
                                <Th>Status</Th>
                                <Th>Primary Impressions</Th>
                                <Th>Secondary Impressions</Th>
                                <Th>Actions</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {studentShifts.map((shift) => (
                                <Tr key={shift.id}>
                                  <Td>{formatDate(getShiftDate(shift))}</Td>
                                  <Td fontSize="xs">{shift.sourceShiftId}</Td>
                                  <Td textAlign="center" fontSize="xs">
                                    {(() => {
                                      const dupInfo = getDuplicateInfo(shift, studentShifts);
                                      if (!dupInfo) return "-";
                                      if (dupInfo.isLatest) {
                                        return (
                                          <Badge colorScheme="green" size="xs">
                                            #{dupInfo.position} ✓
                                          </Badge>
                                        );
                                      }
                                      return (
                                        <Badge colorScheme="orange" size="xs">
                                          #{dupInfo.position}
                                        </Badge>
                                      );
                                    })()}
                                  </Td>
                                  <Td fontSize="sm">{getShiftLocation(shift)}</Td>
                                  <Td textAlign="center">
                                    <Badge colorScheme="purple" size="sm">
                                      {getPatientCount(shift)}
                                    </Badge>
                                  </Td>
                                  <Td textAlign="center" fontSize="lg">
                                    {isShiftLocked(shift) ? "🔒" : ""}
                                  </Td>
                                  <Td fontSize="xs">{formatDate(shift.submissionDate)}</Td>
                                  <Td>
                                    <StatusBadge verdict={shift.auditVerdict} />
                                  </Td>
                                  <Td fontSize="xs">
                                    {extractImpressions([shift], "primary_impression")
                                      .join(", ") || "N/A"}
                                  </Td>
                                  <Td fontSize="xs">
                                    {extractImpressions([shift], "secondary_impression")
                                      .join(", ") || "N/A"}
                                  </Td>
                                  <Td>
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      onClick={() => openShiftDetail(shift)}
                                    >
                                      Review
                                    </Button>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  );
                }
              )}
            </Accordion>
          )}
      </VStack>

      {/* Shift Detail Modal */}
      <ShiftDetailModal
        shift={selectedShift}
        relatedUserFiles={relatedUserFiles || []}
        loadingRelatedFiles={loadingRelatedFiles}
        isOpen={isOpen}
        onClose={onClose}
      />

      {/* Hidden print view */}
      {isPrinting && (
        <Box
          id="printable-report"
          display="none"
          sx={{
            '@media print': {
              display: 'block !important',
            }
          }}
        >
          {/* Header - more compact */}
          <Box textAlign="center" mb={3} pb={2} borderBottom="2px solid black">
            <Heading size="md" mb={1}>
              {cohorts?.find(c => c.id === selectedCohortId)?.displayName} - Clinical Shift Report
            </Heading>
            {dateRange.from && dateRange.to && (
              <Text fontSize="xs" color="gray.600">
                {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
              </Text>
            )}
            <Text fontSize="xs" color="gray.600">
              Generated: {new Date().toLocaleDateString()}
            </Text>
          </Box>

          {/* Two-column layout for students */}
          <Box
            sx={{
              columnCount: 2,
              columnGap: '30px',
              fontSize: '10px',
            }}
          >
            {Object.entries(shiftsByStudent).map(
              ([studentId, { membership, shifts: studentShifts }], index) => {
                return (
                  <Box 
                    key={studentId} 
                    className="print-column" 
                    mb={3}
                    pb={2}
                    borderBottom={index < Object.keys(shiftsByStudent).length - 1 ? "1px solid #ccc" : "none"}
                    sx={{
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    }}
                  >
                    {/* Student name header */}
                    <Text fontWeight="bold" fontSize="12px" mb={2} pb={1} borderBottom="1px solid #666">
                      {membership.user?.firstName && membership.user?.lastName
                        ? `${membership.user.firstName} ${membership.user.lastName}`
                        : membership.userEmail || 'Unknown Student'}
                    </Text>
                    
                    {/* Shift list */}
                    <Box as="ul" pl={3} mb={2} sx={{ listStyleType: 'none' }}>
                      {studentShifts
                        .sort((a, b) => new Date(getShiftDate(a)) - new Date(getShiftDate(b)))
                        .map((shift) => (
                          <Box as="li" key={shift.id} mb={1}>
                            <Text fontSize="9px" fontWeight="semibold" lineHeight="1.1" mb="2px">
                              {formatDate(getShiftDate(shift))}
                            </Text>
                            <Text fontSize="8px" color="gray.600" pl={2} lineHeight="1.1" mb="2px">
                              ID: {shift.sourceShiftId}
                            </Text>
                            <Text fontSize="9px" pl={2} lineHeight="1.1">
                              {getShiftLocation(shift)} • {getPatientCount(shift)} pts •{' '}
                              {shift.auditVerdict === 'pass' ? '✓ Pass' : shift.auditVerdict === 'fail' ? '✗ Fail' : '? Pending'}
                              {isShiftLocked(shift) && ' 🔒'}
                            </Text>
                          </Box>
                        ))}
                    </Box>
                    
                    {/* Student summary stats */}
                    <Text fontSize="8px" color="gray.600" fontStyle="italic" pl={2}>
                      Totals: {studentShifts.length} shifts • {' '}
                      {studentShifts.reduce((sum, s) => sum + getPatientCount(s), 0)} patients • {' '}
                      Pass: {studentShifts.filter(s => s.auditVerdict === 'pass').length} • {' '}
                      Fail: {studentShifts.filter(s => s.auditVerdict === 'fail').length}
                    </Text>
                  </Box>
                );
              }
            )}
          </Box>
          
          <style>{printStyles}</style>
        </Box>
      )}
    </Box>
  );
}
